import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Card,
  Space,
  Breadcrumb,
  Row,
  Col,
  DatePicker,
  Upload,
  Image,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';
import { login } from '../../../redux/slice/userSlice';

const Profile: React.FC = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const user = useSelector((state: any) => state.user?.user);

  // fileList should be an array for antd Upload component
  const [fileList, setFileList] = useState<any[]>([]);

  const [previewImage, setPreviewImage] = useState<string>('');
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        ...user,
        user_dob: user.user_dob ? moment(user.user_dob) : null,
      });

      if (user.user_image) {
        setFileList([
          {
            uid: '-1',
            name: user.user_image,
            status: 'done',
            url: `${process.env.REACT_APP_FILE_URL}uploads/users/profile-image/${user.user_image}`,
          },
        ]);
      } else {
        setFileList([]);
      }
    }
  }, [user, form]);

  // Convert file to base64 preview
  const getBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });

  const handlePreview = async (file: any) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const handleFinish = async (values: any) => {
    try {
      const formData = new FormData();

      // Append fields to FormData - keys should match backend API expected keys
      formData.append('user_id', user?.user_id);
      formData.append('user_name', values.user_name);
      formData.append('user_email', values.user_email);
      formData.append('user_mobile', values.user_mobile);
      formData.append('user_dob', values.user_dob ? values.user_dob.format('YYYY-MM-DD') : '');
      formData.append('user_country', values.user_country || '');
      formData.append('user_address', values.user_address || '');

      // If a new image is uploaded, append the file
      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append('attachments', fileList[0].originFileObj);
      }

      // Send formData instead of JSON
      await apiRequest({
        method: 'put',
        url: 'edit-user',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Refetch user profile after update
      const userProfile = await apiRequest({
        method: 'get',
        url: 'users-by-id',
      });

      dispatch(login(userProfile));
      showSuccess('Profile updated successfully');
      form.resetFields();
      setFileList([]);
    } catch (error) {
      showError('Failed to update profile');
    }
  };

  const uploadButton = (
    <div>
      <UploadOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  const breadcrumbItems = [
    { title: 'Dashboard' },
    { title: 'Profile' },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Breadcrumb items={breadcrumbItems} style={{ marginTop: 6 }} />
      <Row gutter={16} justify="center" style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="User Profile">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleFinish}
              autoComplete="off"
            >
              <Form.Item label="Profile Image" name="user_image">
                <Upload
                  listType="picture-card"
                  fileList={fileList}
                  onPreview={handlePreview}
                  onChange={({ fileList }) => setFileList(fileList)}
                  beforeUpload={() => false} // prevent automatic upload
                  maxCount={1}
                  accept="image/jpeg,image/png,image/webp"
                >
                  {fileList.length >= 1 ? null : uploadButton}
                </Upload>

                <Image
                  preview={{
                    visible: previewOpen,
                    src: previewImage,
                    onVisibleChange: (visible) => {
                      setPreviewOpen(visible);
                      if (!visible) {
                        setPreviewImage('');
                      }
                    },
                  }}
                  style={{ display: 'none' }}
                />
              </Form.Item>
              <Row gutter={16} justify="center" style={{ marginTop: 16 }}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Full Name"
                    name="user_name"
                    rules={[{ required: true, message: 'Please enter your name' }]}
                  >
                    <Input placeholder="Enter full name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Email"
                    name="user_email"
                    rules={[{ required: true, message: 'Please enter your email' }]}
                  >
                    <Input placeholder="Enter email" disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Mobile"
                    name="user_mobile"
                    rules={[{ required: true, message: 'Please enter your mobile number' }]}
                  >
                    <Input placeholder="Enter mobile number" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Date of Birth" name="user_dob">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Country" name="user_country">
                    <Input placeholder="Enter country" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Address" name="user_address">
                    <Input.TextArea rows={3} placeholder="Enter address" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item style={{ textAlign: 'right' }}>
                    <Button type="primary" htmlType="submit">
                      Update Profile
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>
    </Space>
  );
};

export default Profile;
